import {ControlledEZPRSelect} from '../utils/ControlledEZPRSelect.jsx';
import {accountIsBlankOrUnknown, accountTypeOptions} from '../../_helpers';
import {MasterDataDropdown} from '../utils/MasterDataDropdown.jsx';
import {Button, TextField, Tooltip} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {SliderAndTextField} from './SliderAndTextField.jsx';
import DeleteIconWithPopup from '../utils/DeleteIconWithPopup.jsx';
import {
    requestStores,
    useApplicationStore,
} from '../../ListOfPREditors.brick.jsx';
import produce from 'immer';
import {
    filterAccountTypeOptions,
    isEnabledBlankAccountType,
} from '../../../utils/accountDetails.js';
import {
    LAMBDA_NAMES,
    mandatoryFieldsForRequester,
    mandatoryFieldsForSpender,
} from '../../../utils/constants';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {checkLabelValueObject} from '../../../utils/checkObjects';
import {formatCurrencySeparator} from '../../../utils/number';
import {getMasterDataLambda} from '../../../utils/functionSignatures';
import {MasterDataController} from '../../../controllers/MasterDataController';
import TwoToneCheckbox from '../../TwoToneCheckbox.jsx';
import {BrickContext} from 'olympe';
import {useFieldEditability} from '../utils/useFieldEditability.js';

/**
 * @param {AccountLine.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function AccountLine(props) {
    const getMasterData = props.context.get(LAMBDA_NAMES.GET_MASTER_DATA);
    const {isDisabled} = useFieldEditability(props.prTag, {context: props.context});

    const useRequestStore = requestStores.get(props.prTag);

    const [accounts, setAccounts] = useRequestStore((state) => [
        state.accounts,
        state.setAccounts,
    ]);
    const [accountDetailsByMaterial, setAccountDetailsByMaterial] =
    useRequestStore((state) => [
        state.accountDetailsByMaterial,
        state.setAccountDetailsByMaterial,
    ]);
    const areAccountSplitByMaterial = useRequestStore(
        (state) => state.areAccountSplitByMaterial,
    );
    const deleteAccount = useRequestStore((state) => state.deleteAccount);
    const deleteAccountDetailsByMaterial = useRequestStore(
        (state) => state.deleteAccountDetailsByMaterial,
    );
    const materials = useRequestStore((state) => state.materials);
    const userIdToCallSAPAPIs = useRequestStore(
        (state) => state.userIdToCallSAPAPIs,
    );
    const companyCode = useRequestStore((state) => state.companyCode);
    const purchasingGroup = useRequestStore((state) => state.purchasingGroup);
    const plant = useRequestStore((state) => state.defaultPlant);
    const vendorCode = useRequestStore((state) => state.vendorCode);
    const wayOfBuying = useRequestStore(
        (state) => state.wayOfBuying.currentValue,
    );
    const [
        highlightMandatoryFieldForRequester,
        highlightMandatoryFieldForSpender,
    ] = useRequestStore((state) => [
        state.highlightMandatoryFieldForRequester,
        state.highlightMandatoryFieldForSpender,
    ]);

    const accountsUsed = areAccountSplitByMaterial ?
        accountDetailsByMaterial.find((a) => a.material_id === props.materialId)
            .accounts :
        accounts;
    const materialUsed = props.materialId ?
        materials.find((m) => m.id === props.materialId) :
        materials[0];
    const getTotalPriceWithoutCurrencyExchange = useRequestStore(
        (state) => state.getTotalPriceWithoutCurrencyExchange,
    );
    const getTotalMaterialPriceWithoutCurrencyExchange = useRequestStore(
        (state) => state.getTotalMaterialPriceWithoutCurrencyExchange,
    );

    const totalPrice = areAccountSplitByMaterial ?
        getTotalMaterialPriceWithoutCurrencyExchange(props.materialId) :
        getTotalPriceWithoutCurrencyExchange();
    const oldTotalPrice = useRef(null);
    const account = accountsUsed[props.idx];

    const canSplitAccounts = wayOfBuying.value !== 'FO';

    // The account type and allocation type are the same for all account in an account line.
    const accountType = account.accountType;
    const [showConsumptionLevelError, setShowConsumptionLevelError] =
    useState(false);
    const [expectedAmount, setExpectedAmount] = useState(0);
    const [currency, setCurrency] = useState(0);
    const [exceededAmountDetails, setExceededAmountDetails] = useState({
        amount: '',
        currency: '',
        account: '',
    });
    const separator = useApplicationStore((state) => state.userCurrencySeparator);

    const [pendingUpdates, setPendingUpdates] = useState({});
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const isSelected = props.selectedAccounts.some(
        (selectedAccount) =>
            selectedAccount.accountId === props.idx &&
      selectedAccount.materialId === props.materialId,
    );

    const isNotBlank = !accountIsBlankOrUnknown(accountType);

    const isFirstRender = useRef(true);
    const [loadingCheckDependency, setLoadingCheckDependency] = useState(false);

    // Check for WBS exceeded amount error
    useEffect(() => {
        if (
            props.consumptionLevelCheckDetails &&
      props.consumptionLevelCheckDetails.length > 0
        ) {
            const errorFound = props.consumptionLevelCheckDetails.find(
                (error) => error.account === account.wbs.value,
            );

            if (errorFound) {
                setExceededAmountDetails(errorFound);
            } else {
                setExceededAmountDetails({
                    amount: '',
                    currency: '',
                    account: '',
                });
            }
        } else {
            setExceededAmountDetails({
                amount: '',
                currency: '',
                account: '',
            });
        }
    }, [props.consumptionLevelCheckDetails]);

    useEffect(() => {
        if (Number(exceededAmountDetails.amount) !== 0) {
            setShowConsumptionLevelError(true);
        } else {
            setShowConsumptionLevelError(false);
        }
    }, [exceededAmountDetails]);

    // Calculate expected amount and currency
    useEffect(() => {
        let price;
        const allocationType = account.allocationType;
        const totalQuantity = materialUsed.quantity;

        switch (allocationType) {
        case 'percentage':
            price = (props.allocateForValue.value / 100) * totalPrice;
            break;
        case 'price':
            price = props.allocateForValue.value;
            break;
        case 'quantity':
            price = (props.allocateForValue.value / totalQuantity) * totalPrice;
            break;
        default:
            price = props.allocateForValue.value;
            break;
        }
        if (oldTotalPrice.current && oldTotalPrice.current !== totalPrice) {
            props.handleResetAllocateFor();
        }
        oldTotalPrice.current = totalPrice;

        const totalTaxAmount = materials.reduce((total, material) => {
            if (material.taxAmount !== undefined && material.taxAmount !== null) {
                return total + Number(material.taxAmount);
            }
            return total;
        }, 0);

        setExpectedAmount(price + totalTaxAmount);
    }, [
        props.allocateForValue.value,
        account.allocationType,
        totalPrice,
        materialUsed.quantity,
        materials,
    ]);

    useEffect(() => {
        setCurrency(materials[0].currency.value);
    }, [materials]);

    // This useEffect listens for company code changes and checks the dependencies
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (!companyCode.value) {
            return;
        }

        setLoadingCheckDependency(true);

        const wbsPromise = getMasterDataLambda(
            props.context,
            getMasterData,
            'WBS',
            account.wbs.value || '',
            companyCode.value,
            '',
            '',
            '',
            '',
            '',
            userIdToCallSAPAPIs,
        );

        const profitCenterPromise = getMasterDataLambda(
            props.context,
            getMasterData,
            'Profit Center',
            account.profitCenter.value || '',
            companyCode.value,
            '',
            '',
            '',
            '',
            '',
            userIdToCallSAPAPIs,
        );

        const glNumberPromise =
      accountType === 'P' ?
          getMasterDataLambda(
              props.context,
              getMasterData,
              'GL Account filtered by WBS',
              account.glNumber.value || '',
              companyCode.value,
              '',
              account.wbs.value || '',
              '',
              '',
              '',
              userIdToCallSAPAPIs,
          ) :
          getMasterDataLambda(
              props.context,
              getMasterData,
              'GL Account',
              account.glNumber.value || '',
              companyCode.value,
              '',
              '',
              '',
              '',
              '',
              userIdToCallSAPAPIs,
          );

        const costCenterPromise = getMasterDataLambda(
            props.context,
            getMasterData,
            'Cost Center',
            account.costCenter.value || '',
            companyCode.value,
            '',
            '',
            '',
            '',
            '',
            userIdToCallSAPAPIs,
        );

        Promise.all([
            wbsPromise,
            profitCenterPromise,
            glNumberPromise,
            costCenterPromise,
        ])
            .then((values) => {
                const formattedWBS = MasterDataController.formatMasterData(
                    values[0],
                    true,
                    'WBS',
                );

                const formattedProfitCenters = MasterDataController.formatMasterData(
                    values[1],
                    true,
                    'Profit Center',
                );

                const formattedGLAccounts = MasterDataController.formatMasterData(
                    values[2],
                    true,
                    'GL Account',
                );

                const formattedCostCenters = MasterDataController.formatMasterData(
                    values[3],
                    true,
                    'Cost Center',
                );

                if (areAccountSplitByMaterial) {
                    const newAccountDetailsByMaterial = produce(
                        accountDetailsByMaterial,
                        (draft) => {
                            const currentMaterial = draft.find(
                                (item) => item.material_id === props.materialId,
                            );
                            if (currentMaterial) {
                                const currentAccount = currentMaterial.accounts[props.idx];
                                if (currentAccount) {
                                    const wbsExists = formattedWBS.some(
                                        (wbs) => wbs.value === currentAccount.wbs.value,
                                    );
                                    if (!wbsExists) {
                                        currentAccount.wbs = {label: '', value: ''};
                                        currentAccount.deliveryAddress = {label: '', value: ''};
                                        currentAccount.storageLocation = {label: '', value: ''};
                                    }

                                    const profitCenterExists = formattedProfitCenters.some(
                                        (pc) => pc.value === currentAccount.profitCenter.value,
                                    );
                                    if (!profitCenterExists) {
                                        currentAccount.profitCenter = {label: '', value: ''};
                                    }

                                    const glAccountExists = formattedGLAccounts.some(
                                        (gl) => gl.value === currentAccount.glNumber.value,
                                    );
                                    if (!glAccountExists) {
                                        currentAccount.glNumber = {label: '', value: ''};
                                    }

                                    const costCenterExists = formattedCostCenters.some(
                                        (cc) => cc.value === currentAccount.costCenter.value,
                                    );
                                    if (!costCenterExists) {
                                        currentAccount.costCenter = {label: '', value: ''};
                                    }
                                }
                            }
                        },
                    );
                    setAccountDetailsByMaterial(
                        newAccountDetailsByMaterial,
                        props.materialId,
                    );
                } else {
                    const newAccounts = produce(accountsUsed, (draft) => {
                        const currentAccount = draft[props.idx];
                        if (currentAccount) {
                            const wbsExists = formattedWBS.some(
                                (wbs) => wbs.value === currentAccount.wbs.value,
                            );
                            if (!wbsExists) {
                                currentAccount.wbs = {label: '', value: ''};
                                currentAccount.deliveryAddress = {label: '', value: ''};
                                currentAccount.storageLocation = {label: '', value: ''};
                            }

                            const profitCenterExists = formattedProfitCenters.some(
                                (pc) => pc.value === currentAccount.profitCenter.value,
                            );
                            if (!profitCenterExists) {
                                currentAccount.profitCenter = {label: '', value: ''};
                            }

                            const glAccountExists = formattedGLAccounts.some(
                                (gl) => gl.value === currentAccount.glNumber.value,
                            );
                            if (!glAccountExists) {
                                currentAccount.glNumber = {label: '', value: ''};
                            }

                            const costCenterExists = formattedCostCenters.some(
                                (cc) => cc.value === currentAccount.costCenter.value,
                            );
                            if (!costCenterExists) {
                                currentAccount.costCenter = {label: '', value: ''};
                            }
                        }
                    });
                    setAccounts(newAccounts, props.idx);
                }

                setLoadingCheckDependency(false);
            })
            .catch((error) => {
                console.error('Error fetching master data:', error);
                setLoadingCheckDependency(false);
            });
    }, [companyCode.value]);

    const handleCheckboxChange = () => {
        props.onSelectAccount(props.idx, props.materialId, !isSelected);
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setSnackbarOpen(false);
    };

    const snackbarAction = (
        <React.Fragment>
            <Button
                color="secondary"
                size="small"
                onClick={() => handleUpdateAction()}
            >
        YES
            </Button>
            <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleCloseSnackbar}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </React.Fragment>
    );

    const handleUpdateAction = () => {
        props.updateSelectedAccounts(pendingUpdates);
        setPendingUpdates({});
        setSnackbarOpen(false);
    };

    const handleDeleteLine = () => {
        areAccountSplitByMaterial ?
            deleteAccountDetailsByMaterial(props.materialId, props.idx) :
            deleteAccount(props.idx);
        props.deleteAnAllocateForValue(props.idx);
    };

    const handleAccountTypeChange = (_, newValue) => {
        const updates = {accountType: newValue};

        if (areAccountSplitByMaterial) {
            const newAccountDetailsByMaterial = produce(
                accountDetailsByMaterial,
                (draft) => {
                    draft
                        .find((item) => item.material_id === props.materialId)
                        .accounts.forEach(
                            (account) =>
                                (account.accountType = newValue ?? {
                                    label: '',
                                    value: '',
                                }),
                        );
                },
            );
            setAccountDetailsByMaterial(
                newAccountDetailsByMaterial,
                props.materialId,
            );
        } else {
            const newAccounts = produce(accountsUsed, (draft) => {
                draft[props.idx].accountType = newValue ?? {label: '', value: ''};
            });
            setAccounts(newAccounts, props.idx);
        }

        if (areAccountSplitByMaterial && props.selectedAccounts.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    // Reset to initial Account type state
    useEffect(() => {
        if (
            !accountType.value &&
      !isEnabledBlankAccountType(
          vendorCode,
          purchasingGroup.value,
          plant.value,
          materialUsed.type,
      )
        ) {
            const newAccounts = produce(accountsUsed, (draft) => {
                draft[props.idx].accountType = {label: '', value: ''};
            });
            setAccounts(newAccounts, props.idx);
        }
    }, [accountType.value, plant.value, purchasingGroup.value, vendorCode]);

    const handleWbsChange = (newValue) => {
        const updates = {wbs: newValue};

        if (areAccountSplitByMaterial) {
            const newAccountDetailsByMaterial = produce(
                accountDetailsByMaterial,
                (draft) => {
                    draft.find((item) => item.material_id === props.materialId).accounts[
                        props.idx
                    ].wbs = checkLabelValueObject(newValue);
                    draft.find((item) => item.material_id === props.materialId).accounts[
                        props.idx
                    ].accountStatus = newValue.status;
                },
            );
            setAccountDetailsByMaterial(
                newAccountDetailsByMaterial,
                props.materialId,
            );
        } else {
            const newAccounts = produce(accountsUsed, (draft) => {
                draft[props.idx].wbs = checkLabelValueObject(newValue);
                draft[props.idx].accountStatus = newValue.status;
            });
            setAccounts(newAccounts, props.idx);
        }

        if (props.selectedAccounts.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleCostCenterChange = (newValue) => {
        const updates = {costCenter: newValue};

        if (areAccountSplitByMaterial) {
            const newAccountDetailsByMaterial = produce(
                accountDetailsByMaterial,
                (draft) => {
                    draft.find((item) => item.material_id === props.materialId).accounts[
                        props.idx
                    ].costCenter = newValue;
                },
            );
            setAccountDetailsByMaterial(
                newAccountDetailsByMaterial,
                props.materialId,
            );
        } else {
            const newAccounts = produce(accountsUsed, (draft) => {
                draft[props.idx].costCenter = newValue;
            });
            setAccounts(newAccounts, props.idx);
        }

        if (props.selectedAccounts.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleProfitCenterChange = (newValue) => {
        const updates = {profitCenter: newValue};

        if (areAccountSplitByMaterial) {
            const newAccountDetailsByMaterial = produce(
                accountDetailsByMaterial,
                (draft) => {
                    draft.find((item) => item.material_id === props.materialId).accounts[
                        props.idx
                    ].profitCenter = newValue;
                },
            );
            setAccountDetailsByMaterial(
                newAccountDetailsByMaterial,
                props.materialId,
            );
        } else {
            const newAccounts = produce(accountsUsed, (draft) => {
                draft[props.idx].profitCenter = newValue;
            });
            setAccounts(newAccounts, props.idx);
        }

        if (props.selectedAccounts.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleGlNumberChange = (newValue) => {
        const updates = {glNumber: newValue};

        if (areAccountSplitByMaterial) {
            const newAccountDetailsByMaterial = produce(
                accountDetailsByMaterial,
                (draft) => {
                    draft.find((item) => item.material_id === props.materialId).accounts[
                        props.idx
                    ].glNumber = newValue;
                },
            );
            setAccountDetailsByMaterial(
                newAccountDetailsByMaterial,
                props.materialId,
            );
        } else {
            const newAccounts = produce(accountsUsed, (draft) => {
                draft[props.idx].glNumber = newValue;
            });
            setAccounts(newAccounts, props.idx);
        }

        if (props.selectedAccounts.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const handleAccountStatusChange = (event) => {
        const updates = {accountStatus: event.target.value};

        if (areAccountSplitByMaterial) {
            const newAccountDetailsByMaterial = produce(
                accountDetailsByMaterial,
                (draft) => {
                    draft.find((item) => item.material_id === props.materialId).accounts[
                        props.idx
                    ].accountStatus = event.target.value;
                },
            );
            setAccountDetailsByMaterial(
                newAccountDetailsByMaterial,
                props.materialId,
            );
        } else {
            const newAccounts = produce(accountsUsed, (draft) => {
                draft[props.idx].accountStatus = event.target.value;
            });
            setAccounts(newAccounts, props.idx);
        }

        if (props.selectedAccounts.length > 0) {
            setPendingUpdates(updates);
            setSnackbarOpen(true);
        }
    };

    const exceededAmountDecimals =
    exceededAmountDetails.currency === 'USDN' ? 5 : 2;
    return (
        <>
            <div className={'pr-editor-account-details-account-line-container'}>
                {(accountsUsed.length > 1 || areAccountSplitByMaterial) && (
                    <TwoToneCheckbox
                        checked={isSelected}
                        onChange={handleCheckboxChange}
                        disabled={isDisabled('accountLine.select')}
                        sx={{
                            alignSelf: 'baseline',
                        }}
                    />
                )}
                <div className={'pr-editor-account-details-account-line-content'}>
                    <ControlledEZPRSelect
                        handleChange={handleAccountTypeChange}
                        options={filterAccountTypeOptions(
                            accountTypeOptions,
                            vendorCode,
                            companyCode.value,
                            purchasingGroup.value,
                            plant.value,
                            materialUsed.type,
                            wayOfBuying.value,
                        )}
                        value={accountType.label}
                        readOnly={isDisabled('accountLine.accountType')}
                        highlightMandatoryFieldForRequester={
                            highlightMandatoryFieldForRequester
                        }
                        highlightMandatoryFieldForSpender={
                            highlightMandatoryFieldForSpender
                        }
                        mandatoryForSpender={mandatoryFieldsForSpender.includes(
                            'Account type',
                        )}
                        mandatoryForRequester={mandatoryFieldsForRequester.includes(
                            'Account type',
                        )}
                    />
                </div>
                {(accountType.value === 'P' || accountType.value === 'S') &&
        isNotBlank ? (
                        <div className={'pr-editor-account-details-account-line-content'}>
                            <MasterDataDropdown
                                property={'WBS'} // with selectedAccountType being 'P' => WBS Project
                                selectedValue={account.wbs}
                                selectedCompanyCode={companyCode}
                                onChange={handleWbsChange}
                                onBlur={handleWbsChange}
                                context={props.context}
                                getMasterData={getMasterData}
                                filterQueryWithSearch={true}
                                userId={userIdToCallSAPAPIs}
                                recentlyUsed
                                disabled={isDisabled('accountLine.wbs')}
                                highlightMandatoryFieldForRequester={
                                    highlightMandatoryFieldForRequester
                                }
                                highlightMandatoryFieldForSpender={
                                    highlightMandatoryFieldForSpender
                                }
                                mandatoryForSpender={mandatoryFieldsForSpender.includes('WBS')}
                                mandatoryForRequester={mandatoryFieldsForRequester.includes(
                                    'WBS',
                                )}
                                loading={loadingCheckDependency}
                            />
                        </div>
                    ) : undefined}
                {(accountType.value === 'K' || accountType.value === 'S') &&
        isNotBlank ? (
                        <div className={'pr-editor-account-details-account-line-content'}>
                            <MasterDataDropdown
                                property={'Cost Center'} // with selectedAccountType being 'K' => Cost Center
                                selectedValue={account.costCenter}
                                onChange={handleCostCenterChange}
                                onBlur={handleCostCenterChange}
                                context={props.context}
                                getMasterData={getMasterData}
                                selectedCompanyCode={companyCode}
                                filterQueryWithSearch={true}
                                userId={userIdToCallSAPAPIs}
                                recentlyUsed
                                disabled={isDisabled('accountLine.costCenter')}
                                highlightMandatoryFieldForRequester={
                                    highlightMandatoryFieldForRequester
                                }
                                highlightMandatoryFieldForSpender={
                                    highlightMandatoryFieldForSpender
                                }
                                mandatoryForSpender={mandatoryFieldsForSpender.includes(
                                    'Cost center',
                                )}
                                mandatoryForRequester={mandatoryFieldsForRequester.includes(
                                    'Cost center',
                                )}
                                loading={loadingCheckDependency}
                                renderOption={(propsOption, option) => (
                                    <li {...propsOption} data-cy={`costCenter_field_option`}>
                                        {option.label}
                                    </li>
                                )}
                            />
                        </div>
                    ) : undefined}
                {accountType.value === '2' && isNotBlank ? (
                    <div className={'pr-editor-account-details-account-line-content'}>
                        <MasterDataDropdown
                            property={'Profit Center'} // with selectedAccountType not being '2' (other) => Profit Center
                            selectedValue={account.profitCenter}
                            onChange={handleProfitCenterChange}
                            onBlur={handleProfitCenterChange}
                            context={props.context}
                            getMasterData={getMasterData}
                            selectedCompanyCode={companyCode}
                            filterQueryWithSearch={true}
                            userId={userIdToCallSAPAPIs}
                            recentlyUsed
                            disabled={isDisabled('accountLine.profitCenter')}
                            highlightMandatoryFieldForRequester={
                                highlightMandatoryFieldForRequester
                            }
                            highlightMandatoryFieldForSpender={
                                highlightMandatoryFieldForSpender
                            }
                            mandatoryForSpender={mandatoryFieldsForSpender.includes(
                                'Profit center',
                            )}
                            mandatoryForRequester={mandatoryFieldsForRequester.includes(
                                'Profit center',
                            )}
                            loading={loadingCheckDependency}
                        />
                    </div>
                ) : undefined}
                {isNotBlank ? (
                    <div className={'pr-editor-account-details-account-line-content'}>
                        <MasterDataDropdown
                            property={
                                accountType.value === 'P' ?
                                    'GL Account filtered by WBS' :
                                    'GL Account'
                            }
                            selectedValue={account.glNumber}
                            onChange={handleGlNumberChange}
                            onBlur={handleGlNumberChange}
                            context={props.context}
                            getMasterData={getMasterData}
                            selectedCompanyCode={companyCode}
                            selectedWBS={accountType.value === 'P' ? account.wbs : undefined}
                            filterQueryWithSearch={true}
                            userId={userIdToCallSAPAPIs}
                            recentlyUsed
                            disabled={isDisabled('accountLine.glNumber')}
                            highlightMandatoryFieldForRequester={
                                highlightMandatoryFieldForRequester
                            }
                            highlightMandatoryFieldForSpender={
                                highlightMandatoryFieldForSpender
                            }
                            mandatoryForSpender={mandatoryFieldsForSpender.includes(
                                'GL account',
                            )}
                            mandatoryForRequester={mandatoryFieldsForRequester.includes(
                                'GL account',
                            )}
                            loading={loadingCheckDependency}
                        />
                    </div>
                ) : undefined}
                {canSplitAccounts && isNotBlank ? (
                    <SliderAndTextField
                        allocateForValue={props.allocateForValue}
                        handleAllocateForChange={props.handleAllocateForChange}
                        idx={props.idx}
                        sliderWrongAmountTooltipText={props.sliderWrongAmountTooltipText}
                        disabled={
                            accountsUsed?.length === 1 ||
                            isDisabled('accountLine.allocation') ||
                            props.allocateForValue?.disabled
                        }
                        min={0}
                        max={props.total}
                        allocationType={account.allocationType}
                        totalPrice={totalPrice}
                        totalQuantity={Number(materialUsed.quantity)}
                        currency={materialUsed.currency.value}
                        allocationTotalError={props.allocationTotalError}
                    />
                ) : undefined}
                {isNotBlank ? (
                    <div className={'pr-editor-account-details-account-line-content'}>
                        <TextField
                            name={'accountStatus'}
                            size={'small'}
                            sx={{width: '100%'}}
                            disabled
                            value={account.accountStatus}
                            onBlur={handleAccountStatusChange}
                        />
                    </div>
                ) : undefined}
                {accountType.value === 'P' && isNotBlank ? (
                    <div className={'pr-editor-account-details-account-line-content'}>
                        <TextField
                            name={'expectedAmount'}
                            size={'small'}
                            disabled
                            placeholder={'2,000 USD)'}
                            value={`${formatCurrencySeparator(
                                expectedAmount.toString(),
                                separator,
                                currency === 'USDN' ? 5 : 2,
                            )} ${currency}`}
                            sx={{
                                width: '100%',
                            }}
                        />
                    </div>
                ) : undefined}
                {accountType.value === 'P' && isNotBlank ? (
                    <div className={'pr-editor-account-details-account-line-content'}>
                        {showConsumptionLevelError ? (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Tooltip
                                    disableFocusListener
                                    disableTouchListener
                                    title={`Budget was exceeded by ${formatCurrencySeparator(
                                        exceededAmountDetails.amount,
                                        separator,
                                        exceededAmountDecimals,
                                    )} ${exceededAmountDetails.currency} for the WBS element ${
                                        exceededAmountDetails.account
                                    }`}
                                >
                                    <TextField
                                        name={'exceededAmount'}
                                        size={'small'}
                                        disabled
                                        placeholder={''}
                                        value={
                                            Number(exceededAmountDetails.amount) === 0 ?
                                                `0 ${currency}` :
                                                `${formatCurrencySeparator(
                                                    exceededAmountDetails.amount,
                                                    separator,
                                                    exceededAmountDecimals,
                                                )} ${exceededAmountDetails.currency}`
                                        }
                                        sx={{
                                            'width': '100%',
                                            '& .MuiOutlinedInput-root.Mui-disabled': {
                                                'border': '1px solid #f44336',
                                                '&:hover fieldset': {
                                                    borderColor: 'transparent',
                                                },
                                            },
                                        }}
                                    />
                                </Tooltip>
                                <ErrorOutlineIcon
                                    color="error"
                                    fontSize="large"
                                    sx={{marginLeft: '0.5rem'}}
                                />
                            </div>
                        ) : (props.consumptionLevelCheckStatus === 'loading' ||
                props.consumptionLevelCheckStatus === 'success') &&
              account.wbs.value.startsWith('AC-') ? (
                                <TextField
                                    name={'exceededAmount'}
                                    size={'small'}
                                    disabled
                                    placeholder={''}
                                    value={
                                        Number(exceededAmountDetails.amount) === 0 ?
                                            `0 ${currency}` :
                                            `${formatCurrencySeparator(
                                                exceededAmountDetails.amount,
                                                separator,
                                                exceededAmountDecimals,
                                            )} ${exceededAmountDetails.currency}`
                                    }
                                    sx={{
                                        'width': '100%',
                                        '& .MuiOutlinedInput-root.Mui-disabled': {
                                            'border': '1px solid #4CAF50',
                                            '&:hover fieldset': {
                                                borderColor: 'transparent',
                                            },
                                        },
                                    }}
                                />
                            ) : (
                                <TextField
                                    name={'exceededAmount'}
                                    size={'small'}
                                    disabled
                                    placeholder={''}
                                    value={
                                        Number(exceededAmountDetails.amount) === 0 ?
                                            `0 ${currency}` :
                                            `${formatCurrencySeparator(
                                                exceededAmountDetails.amount,
                                                separator,
                                                exceededAmountDecimals,
                                            )} ${exceededAmountDetails.currency}`
                                    }
                                    sx={{
                                        width: '100%',
                                    }}
                                />
                            )}
                    </div>
                ) : (
                    <></>
                )}
                {isNotBlank ? (
                    <div className={'pr-editor-account-details-account-line-content'}>
                        {props.accountsCount > 1 ? (
                            <DeleteIconWithPopup
                                onConfirm={handleDeleteLine}
                                buttonProps={{
                                    disabled: isDisabled('accountLine.delete'),
                                }}
                            />
                        ) : (
                            <div style={{width: '40px'}} />
                        )}
                    </div>
                ) : undefined}
            </div>
            <Snackbar
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                message={'Do you want to modify all selected account lines?'}
                action={snackbarAction}
            />
        </>
    );
}

AccountLine.propTypes = {
    idx: PropTypes.number,
    materialId: PropTypes.number,
    allocateForValue: PropTypes.object,
    handleAllocateForChange: PropTypes.func,
    handleResetAllocateFor: PropTypes.func,
    sliderWrongAmountTooltipText: PropTypes.string,
    deleteAnAllocateForValue: PropTypes.func,
    total: PropTypes.number,
    prTag: PropTypes.string,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    accountsCount: PropTypes.number,
    selectedAccounts: PropTypes.array,
    onSelectAccount: PropTypes.func,
    updateSelectedAccounts: PropTypes.func,
    consumptionLevelCheckDetails: PropTypes.array,
    consumptionLevelCheckStatus: PropTypes.string,
    allocationTotalError: PropTypes.bool,
};
